import { ReactNode } from 'react';
import tw from 'tailwind/tailwind-theme-gentux';

import useOptimizelyData, { MaybeUserBuckets } from 'hooks/useOptimizelyData';
import {
  EXPERIMENT_HOMEPAGE_SHOWROOM_CTA,
  EXPERIMENT_HOMEPAGE_SHOWROOM_CTA_PARAGRAPH,
  EXPERIMENT_HOMEPAGE_SHOWROOM_CTA_BULLETED,
} from 'constants/experiments';
import Line from 'components/Line';
import Layout2x1ImageRight from 'components/Layout2x1ImageRight';
import TrackedLink from 'components/sanity/TrackedLink';

const testGroups = [EXPERIMENT_HOMEPAGE_SHOWROOM_CTA_PARAGRAPH, EXPERIMENT_HOMEPAGE_SHOWROOM_CTA_BULLETED] as const;

const belongsToTestGroup = (buckets: MaybeUserBuckets) =>
  testGroups.some((bucket) => bucket === buckets?.[EXPERIMENT_HOMEPAGE_SHOWROOM_CTA]);

const belongsToParagraphVariant = (bucket: string | null) => bucket === EXPERIMENT_HOMEPAGE_SHOWROOM_CTA_PARAGRAPH;

const belongsToBulletListVariant = (bucket: string | null) => bucket === EXPERIMENT_HOMEPAGE_SHOWROOM_CTA_BULLETED;

const ShowroomImage = () => {
  return (
    <div className="flex justify-center">
      <picture className="inline h-auto w-auto">
        <source
          key="lg-up"
          media={`(min-width: ${tw.screens.lg})`}
          width={1024}
          srcSet="https://gentux.imgix.net/showroom.jpg?auto=format,compress&w=1024"
        />

        <source key="base" width={640} srcSet="https://gentux.imgix.net/showroom.jpg?auto=format&w=640" />

        <img className="block h-full object-cover xl:max-w-[700px]" alt="A photograph of a Generation Tux showroom" />
      </picture>
    </div>
  );
};

type ShowroomCalloutProps = {
  heading: string;
  subheading: string;
  copyParagraph: ReactNode;
  copyList: ReactNode[];
};

const ShowroomCallout = ({ heading, subheading, copyParagraph, copyList }: ShowroomCalloutProps) => {
  const optimizely = useOptimizelyData();

  if (!optimizely.loaded || !belongsToTestGroup(optimizely.data)) {
    return null;
  }

  const bucket = optimizely.data?.homepageShowroomCtaTest ?? null;

  return (
    <div className="optimizely--homepage-showroom-callout p-section-hero">
      <h2 className="optimizely--homepage-showroom-callout__heading text-h2-display pb-64 text-center text-black sm:pb-100 md:text-h1-display">
        {heading}
      </h2>

      <Layout2x1ImageRight
        hero
        className="w-full max-w-full px-16"
        contentClassName="flex flex-col justify-center h-full"
        image={<ShowroomImage />}
      >
        <h3 className="optimizely--homepage-showroom-callout__subheading text-h2-display text-3xl md:text-4xl lg:text-[40px]">
          {subheading}
        </h3>

        <Line />

        {belongsToParagraphVariant(bucket) ? (
          <div className="optimizely--homepage-showroom-callout__content--paragraph text-md mb-16 md:text-xl">{copyParagraph}</div>
        ) : belongsToBulletListVariant(bucket) ? (
          <ul className="list-disc-lg optimizely--homepage-showroom-callout__content--list">
            {copyList.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        ) : null}

        <TrackedLink
          href="/showrooms"
          className="btn-info optimizely--homepage-showroom-callout__button btn !mt-32 sm:text-md lg:w-1/2 xs-only:w-1/2"
          reloadDocument
        >
          Schedule Your Visit
        </TrackedLink>
      </Layout2x1ImageRight>
    </div>
  );
};

export default ShowroomCallout;
